import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { isValid, parse } from 'date-fns';
import de from 'date-fns/locale/de';
import enCA from 'date-fns/locale/en-CA';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import fr from 'date-fns/locale/fr';
import frCA from 'date-fns/locale/fr-CA';

import { DateTimeLocaleProvider } from './datetime-locale-provider';

// TODO make configurable
const FORMAT_DATETIME_STR = 'LLL-d-yyyy, h:mm a';
const FORMAT_DATE_STR = 'LLL-d-yyyy';
const FORMAT_TIME_STR = 'h:mm a';

interface Opts {
    onlyDate?: boolean;
    onlyTime?: boolean;
}

function getFormatStr(opts: Opts): string {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (opts && opts.onlyDate) {
        return FORMAT_DATE_STR;
    }
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (opts && opts.onlyTime) {
        return FORMAT_TIME_STR;
    }
    return FORMAT_DATETIME_STR;
}

const localeToDateLocaleMap = {
    en: enUS,
    'en-CA': enCA,
    fr,
    'fr-CA': frCA,
    de,
    es,
};

/**
 * angular's locale seems to be more accurate than date-fn's.
 * fr locale from date-fn seems to have incorrect period (am/pm etc) localization values.
 *
 * if date-fns locales lined up with angular's date-fns' format() would be used
 *
 * only date-fns' parse() is used. date pipe will be used when formatting
 */
@Injectable({ providedIn: 'root' })
export class DateTimeParserFormatter {
    private readonly _locale: Locale;
    private readonly _dp: DatePipe;

    constructor(private readonly _localeProvider: DateTimeLocaleProvider) {
        const locale = _localeProvider.locale();
        this._locale = localeToDateLocaleMap[locale];
        this._dp = new DatePipe(locale);
    }

    format(value: Date, opts: Opts): string {
        const fmtStr = getFormatStr(opts);
        return !isValid(value) ? '' : this._dp.transform(value, fmtStr);
    }

    parse(value: string, opts: Opts): Date {
        const fmtStr = getFormatStr(opts);
        // see this class' jsdoc. the only localized part of the string expected is the period. see FORMAT_*_STR
        if (value) {
            value = value.replace(/a(\.|)m(\.|)/i, 'AM');
            value = value.replace(/p(\.|)m(\.|)/i, 'PM');

            if (this._locale.code === enCA.code) {
                // replace the dot in abbreviated months since date-fn's locale doesn't use it
                value = value.replace(/(^[a-zA-Z].+?)\.([-\s])/i, '$1$2');
            }
        }
        return parse(value, fmtStr, new Date(), { locale: this._locale });
    }
}
